import { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CartItem } from "@/contexts/CartContext";
import { validateCPF, formatCPF, formatPhone, normalizePhoneDigits } from "@/lib/validators";
import { toast } from "@/hooks/use-toast";
import { BuyerData } from "./CheckoutStepBuyer";
import { hasQuestionAnswer, isQuestionAvailableForTiers, parseQuestionAnswer, serializeQuestionAnswer, validateQuestionOptions, normalizeQuestionOptions } from "@/lib/checkout-form";
import { ArrowLeft, Check, User } from "lucide-react";
import { Loader2 } from "lucide-react";
import { formatBRL } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { logValidationFailure } from "@/lib/system-logger";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AttendeeEntry {
  name: string;
  email: string;
  cpf: string;
  phone: string;
}

const EMPTY_ATTENDEE: AttendeeEntry = { name: "", email: "", cpf: "", phone: "" };

function isAttendeeEmpty(entry?: AttendeeEntry | null): boolean {
  if (!entry) return true;
  return !entry.name?.trim() && !entry.email?.trim() && !entry.cpf?.trim() && !entry.phone?.trim();
}

interface CheckoutStepDataProps {
  items: CartItem[];
  orderQuestions: any[];
  attendeeQuestions: any[];
  attendeeData: Record<string, AttendeeEntry>;
  setAttendeeData: React.Dispatch<React.SetStateAction<Record<string, AttendeeEntry>>>;
  questionAnswers: Record<string, string>;
  setQuestionAnswers: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onNext: () => void;
  onBack?: () => void;
  isLoading: boolean;
  buyerData?: BuyerData;
}

export function CheckoutStepData({
  items,
  orderQuestions,
  attendeeQuestions,
  attendeeData,
  setAttendeeData,
  questionAnswers,
  setQuestionAnswers,
  onNext,
  onBack,
  isLoading,
  buyerData,
}: CheckoutStepDataProps) {
  const { t } = useTranslation("checkout");
  const ticketItems = items.filter((item) => !item.tierId.startsWith("product-"));
  const ticketTierIds = ticketItems.map((item) => item.tierId);
  const [fieldErrors, setFieldErrors] = useState<Record<string, Record<string, string>>>({});

  const totalTickets = useMemo(
    () => ticketItems.reduce((sum, item) => sum + item.quantity, 0),
    [ticketItems],
  );

  const firstTicketKey = ticketItems.length > 0 ? `${ticketItems[0].tierId}-0` : null;

  const visibleOrderQuestions = orderQuestions.filter((question) => isQuestionAvailableForTiers(question, ticketTierIds));
  const visibleAttendeeQuestions = attendeeQuestions;

  const clearError = (key: string, field: string) =>
    setFieldErrors((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: "" } }));

  const handleFillFromBuyer = (key: string) => {
    if (!buyerData) return;
    setAttendeeData((prev) => ({
      ...prev,
      [key]: {
        name: buyerData.fullName,
        email: buyerData.email,
        cpf: buyerData.cpf,
        phone: buyerData.phone,
      },
    }));
    setFieldErrors((p) => ({ ...p, [key]: {} }));
  };

  // 1 ingresso: auto-preenche com dados do comprador se o card ainda estiver vazio
  useEffect(() => {
    if (!buyerData || !firstTicketKey || totalTickets !== 1) return;
    if (!isAttendeeEmpty(attendeeData[firstTicketKey])) return;
    if (!buyerData.fullName?.trim() && !buyerData.phone?.trim()) return;

    setAttendeeData((prev) => {
      if (!isAttendeeEmpty(prev[firstTicketKey])) return prev;
      return {
        ...prev,
        [firstTicketKey]: {
          name: buyerData.fullName || "",
          email: buyerData.email || "",
          cpf: buyerData.cpf || "",
          phone: buyerData.phone || "",
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage a buyer/total; não reescreve se user editou
  }, [buyerData?.fullName, buyerData?.email, buyerData?.cpf, buyerData?.phone, firstTicketKey, totalTickets]);

  const renderQuestionField = (q: any, key: string) => {
    const value = questionAnswers[key] || "";
    const onChange = (val: string) => setQuestionAnswers((prev) => ({ ...prev, [key]: val }));

    switch (q.field_type) {
      case "textarea":
        return <Textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={t("checkout.attendeeSection.answerPlaceholder")} rows={2} maxLength={1000} />;
      case "select":
        return (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger><SelectValue placeholder={t("checkout.attendeeSection.selectPlaceholder")} /></SelectTrigger>
            <SelectContent>
              {normalizeQuestionOptions(q.options).map((opt: string) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "radio":
        return (
          <RadioGroup value={value} onValueChange={onChange} className="space-y-1">
            {normalizeQuestionOptions(q.options).map((opt: string, i: number) => (
              <div key={`${opt}-${i}`} className="flex items-center gap-2">
                <RadioGroupItem value={opt} id={`${key}-${opt}-${i}`} />
                <Label htmlFor={`${key}-${opt}-${i}`} className="text-sm font-normal cursor-pointer">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      case "checkbox":
        return (
          <div className="space-y-2">
            {normalizeQuestionOptions(q.options).map((opt: string, i: number) => {
              const selected = parseQuestionAnswer(q.field_type, value) as string[];
              const checked = selected.includes(opt);
              return (
                <label key={`${opt}-${i}`} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={checked} onCheckedChange={(c) => {
                    const newSel = c ? [...selected, opt] : selected.filter((s) => s !== opt);
                    onChange(serializeQuestionAnswer(q.field_type, newSel));
                  }} />
                  <span className="text-sm font-normal">{opt}</span>
                </label>
              );
            })}
          </div>
        );
      case "date":
        return <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} />;
      default: {
        let opts = q.options;
        if (typeof opts === "string") { try { opts = JSON.parse(opts); } catch {} }
        const isValidation = q.field_type === "text" && (opts?.isValidationEnabled === true || opts?.is_validation_enabled === true);
        return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={isValidation ? (q.question || t("checkout.attendeeSection.textValidationPlaceholder")) : t("checkout.attendeeSection.answerPlaceholder")} maxLength={500} />;
      }
    }
  };

  const handleValidateAndNext = () => {
    const newErrors: Record<string, Record<string, string>> = {};
    let hasError = false;

    for (const item of ticketItems) {
      for (let qi = 0; qi < item.quantity; qi++) {
        const key = `${item.tierId}-${qi}`;
        const data = attendeeData[key] || EMPTY_ATTENDEE;
        const errs: Record<string, string> = {};

        if (!data.name?.trim()) { errs.name = t("checkout.validation.fillName"); hasError = true; }
        if (!data.email?.trim() || !EMAIL_REGEX.test(data.email)) { errs.email = t("checkout.validation.invalidEmail"); hasError = true; }
        if (!data.cpf?.trim()) { errs.cpf = t("checkout.validation.fillCpf"); hasError = true; }
        else if (!validateCPF(data.cpf)) { errs.cpf = t("checkout.validation.invalidCpf"); hasError = true; }
        const phoneDigits = normalizePhoneDigits(data.phone);
        if (!phoneDigits || phoneDigits.length < 10) {
          errs.phone = t("checkout.validation.invalidPhone");
          hasError = true;
        }

        if (Object.keys(errs).length) newErrors[key] = errs;
      }
    }

    for (const q of visibleOrderQuestions) {
      const key = `order-${q.id}`;
      const answer = questionAnswers[key];
      if (q.is_required && !hasQuestionAnswer(q.field_type, answer)) {
        toast({ title: t("checkout.toastsAndErrors.requiredQuestion"), description: t("checkout.toastsAndErrors.answerQuestion", { question: q.question }), variant: "destructive" });
        logValidationFailure({
          eventId: items[0]?.eventId,
          userEmail: buyerData.email,
          questionId: q.id,
          questionTitle: q.question,
          attemptedValue: typeof answer === "string" ? answer : JSON.stringify(answer),
          reason: "required_missing"
        });
        return;
      }
      if (validateQuestionOptions(q, answer)) {
        toast({ title: t("checkout.toastsAndErrors.invalidCode"), description: t("checkout.toastsAndErrors.answerNotValid", { question: q.question }), variant: "destructive" });
        logValidationFailure({
          eventId: items[0]?.eventId,
          userEmail: buyerData.email,
          questionId: q.id,
          questionTitle: q.question,
          attemptedValue: typeof answer === "string" ? answer : JSON.stringify(answer),
          reason: "code_not_found"
        });
        return;
      }
    }

    for (const item of ticketItems) {
      for (let qi = 0; qi < item.quantity; qi++) {
        const relevantQuestions = visibleAttendeeQuestions.filter((question) => isQuestionAvailableForTiers(question, [item.tierId]));
        for (const q of relevantQuestions) {
          const key = `attendee-${item.tierId}-${qi}-${q.id}`;
          const answer = questionAnswers[key];
          if (q.is_required && !hasQuestionAnswer(q.field_type, answer)) {
            toast({ title: t("checkout.toastsAndErrors.requiredQuestion"), description: t("checkout.toastsAndErrors.answerQuestionTicket", { question: q.question, tierName: item.tierName, number: qi + 1 }), variant: "destructive" });
            logValidationFailure({
              eventId: item.eventId,
              userEmail: buyerData.email,
              questionId: q.id,
              questionTitle: q.question,
              attemptedValue: typeof answer === "string" ? answer : JSON.stringify(answer),
              reason: "required_missing"
            });
            return;
          }
          if (validateQuestionOptions(q, answer)) {
            toast({ title: t("checkout.toastsAndErrors.invalidCode"), description: t("checkout.toastsAndErrors.answerNotValidTicket", { question: q.question, tierName: item.tierName }), variant: "destructive" });
            logValidationFailure({
              eventId: item.eventId,
              userEmail: buyerData.email,
              questionId: q.id,
              questionTitle: q.question,
              attemptedValue: typeof answer === "string" ? answer : JSON.stringify(answer),
              reason: "code_not_found"
            });
            return;
          }
        }
      }
    }

    setFieldErrors(newErrors);
    if (hasError) return;
    onNext();
  };

  // Count ticket index across all items for display
  let globalTicketIdx = 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-[22px] font-bold tracking-tight text-foreground">{t("checkout.attendeeSection.title")}</h2>
          <p className="text-[13px] text-muted-foreground mt-1">{t("checkout.attendeeSection.subtitle")}</p>
        </div>
        {buyerData && firstTicketKey && (
          <button
            type="button"
            title={t("checkout.attendeeSection.fillFromProfileTooltip")}
            onClick={() => handleFillFromBuyer(firstTicketKey)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-border bg-transparent text-xs font-semibold text-foreground cursor-pointer whitespace-nowrap shrink-0 hover:bg-muted transition-colors"
          >
            <User className="h-3 w-3" />
            {t("checkout.attendeeSection.fillFromProfile")}
          </button>
        )}
      </div>

      {/* Order-level questions */}
      {visibleOrderQuestions.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("checkout.attendeeSection.orderInfo")}</p>
          {visibleOrderQuestions.map((q: any) => (
            <div key={q.id} className="flex flex-col gap-1">
              <Label className="text-xs font-semibold">{q.question}{q.is_required ? <span className="text-primary ml-0.5">*</span> : ""}</Label>
              {renderQuestionField(q, `order-${q.id}`)}
            </div>
          ))}
        </div>
      )}

      {/* Ticket cards */}
      {ticketItems.map((item) =>
        Array.from({ length: item.quantity }).map((_, qi) => {
          const key = `${item.tierId}-${qi}`;
          const data = attendeeData[key] || EMPTY_ATTENDEE;
          const errs = fieldErrors[key] || {};
          const relevantAttendeeQuestions = visibleAttendeeQuestions.filter((q) => isQuestionAvailableForTiers(q, [item.tierId]));
          globalTicketIdx += 1;
          const ticketLabel = t("checkout.attendeeSection.ticketLabel", { number: globalTicketIdx });

          return (
            <div key={key} className="rounded-xl border border-border bg-card overflow-hidden">
              {/* Ticket header */}
              <div className="px-5 py-[13px] border-b border-border flex items-center gap-3" style={{ backgroundColor: '#f0f0f0', boxShadow: 'inset 3px 0 0 hsl(var(--primary))' }}>
                <div>
                  <div className="text-[13px] font-bold">
                    {ticketLabel}{" "}
                    <span className="font-normal text-muted-foreground">— {item.tierName}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {item.price === 0 ? t("checkout.attendeeSection.free") : formatBRL(item.price)}
                  </div>
                </div>
              </div>

              {/* Form body */}
              <div className="p-5 flex flex-col gap-4">
                {/* Nome completo */}
                <div className="flex flex-col gap-1">
                  <Label className="text-xs font-semibold">
                    {t("checkout.attendeeSection.attendeeFullName")} <span className="text-primary">*</span>
                  </Label>
                  <Input
                    value={data.name}
                    onChange={(e) => { setAttendeeData((p) => ({ ...p, [key]: { ...data, name: e.target.value } })); clearError(key, "name"); }}
                    placeholder={t("checkout.buyerSection.fullNamePlaceholder")}
                    className={errs.name ? "border-destructive focus-visible:ring-destructive" : ""}
                  />
                  {errs.name && <span className="text-[11px] text-destructive">{errs.name}</span>}
                </div>

                {/* CPF + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs font-semibold">
                      {t("checkout.attendeeSection.attendeeCpf")} <span className="text-primary">*</span>
                    </Label>
                    <Input
                      value={data.cpf}
                      onChange={(e) => { setAttendeeData((p) => ({ ...p, [key]: { ...data, cpf: formatCPF(e.target.value) } })); clearError(key, "cpf"); }}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      inputMode="numeric"
                      autoComplete="off"
                      className={errs.cpf ? "border-destructive focus-visible:ring-destructive" : ""}
                    />
                    {errs.cpf && <span className="text-[11px] text-destructive">{errs.cpf}</span>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs font-semibold">
                      {t("checkout.attendeeSection.attendeeEmail")} <span className="text-primary">*</span>
                    </Label>
                    <Input
                      type="email"
                      value={data.email}
                      onChange={(e) => { setAttendeeData((p) => ({ ...p, [key]: { ...data, email: e.target.value } })); clearError(key, "email"); }}
                      placeholder={t("guestClaim.emailPlaceholder")}
                      className={errs.email ? "border-destructive focus-visible:ring-destructive" : ""}
                    />
                    {errs.email && <span className="text-[11px] text-destructive">{errs.email}</span>}
                  </div>
                </div>

                {/* Telefone */}
                <div className="flex flex-col gap-1">
                  <Label className="text-xs font-semibold">
                    {t("checkout.attendeeSection.attendeePhone")} <span className="text-primary">*</span>
                  </Label>
                  <Input
                    value={data.phone}
                    onChange={(e) => {
                      setAttendeeData((p) => ({ ...p, [key]: { ...data, phone: formatPhone(e.target.value) } }));
                      clearError(key, "phone");
                    }}
                    placeholder="(11) 99999-9999"
                    maxLength={15}
                    inputMode="tel"
                    autoComplete="tel"
                    className={errs.phone ? "border-destructive focus-visible:ring-destructive" : ""}
                  />
                  {errs.phone && <span className="text-[11px] text-destructive">{errs.phone}</span>}
                </div>

                {/* Attendee-level questions */}
                {relevantAttendeeQuestions.length > 0 && (
                  <div className="space-y-3 pt-2 border-t border-border">
                    {relevantAttendeeQuestions.map((q: any) => (
                      <div key={q.id} className="flex flex-col gap-1">
                        <Label className="text-xs font-semibold">
                          {q.question}{q.is_required && <span className="text-primary ml-0.5">*</span>}
                        </Label>
                        {renderQuestionField(q, `attendee-${key}-${q.id}`)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Bottom nav */}
      <div className="flex items-center justify-between mt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl border border-border bg-transparent text-[14px] font-medium text-foreground cursor-pointer hover:bg-muted transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("checkout.attendeeSection.back")}
        </button>
        <button
          type="button"
          onClick={handleValidateAndNext}
          disabled={isLoading}
          className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-primary text-white border-none text-[15px] font-semibold cursor-pointer shadow-sm shadow-black/15 transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {isLoading ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> {t("checkout.attendeeSection.creatingOrder")}</>
          ) : (
            <>{t("checkout.attendeeSection.confirmOrder")} <Check className="h-4 w-4 stroke-[2.5]" /></>
          )}
        </button>
      </div>
    </div>
  );
}
